import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { departments as ucDepartments, workflows as ucWorkflows } from '../data/useCasesData';

const Header = ({ variant = 'light' }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  // Mobile nav is a single-open accordion (7.1): only one dropdown section is
  // expanded at a time so the 7-item / 3-dropdown bar stays compact on small screens.
  const [mobileSection, setMobileSection] = useState(null);
  const dropdownRef = useRef(null);

  const toggleMobileSection = (id) =>
    setMobileSection((prev) => (prev === id ? null : id));

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileSection(null);
  };

  // variant: 'light' (default) for the light aurora — the header is TRANSPARENT in the
  // hero with dark text/logo, and turns solid white on scroll. 'dark' is for dark
  // backgrounds — white text/logo in the hero (landing pages render their own chrome).
  const isDarkBg = variant === 'dark';

  // Agent data with slug URLs and individual images
  const agents = [
    { id: 'buzz', name: 'Buzz', role: 'Social Media Expert', slug: '/buzz-ai-social-media-expert', image: '/images/agent-thumbs/buzz.webp' },
    { id: 'dora', name: 'Dora', role: 'Research & Data Analyst', slug: '/dora-ai-research-data-analyst', image: '/images/agent-thumbs/dora.webp' },
    { id: 'finn', name: 'Finn', role: 'Finance Expert', slug: '/finn-ai-finance-expert', image: '/images/agent-thumbs/finn.webp' },
    { id: 'luc', name: 'Luc', role: 'Personal Growth Partner', slug: '/luc-ai-personal-growth-partner', image: '/images/agent-thumbs/luc.webp' },
    { id: 'marsi', name: 'Marsi', role: 'Marketing and Growth Expert', slug: '/marsi-ai-marketing-growth-expert', image: '/images/agent-thumbs/marsi.webp' },
    { id: 'sally', name: 'Sally', role: 'Sales Expert', slug: '/sally-ai-sales-expert', image: '/images/agent-thumbs/sally.webp' },
    { id: 'stark', name: 'Stark', role: 'Business Strategist', slug: '/stark-ai-business-strategist', image: '/images/agent-thumbs/stark.webp' },
    { id: 'tali', name: 'Tali', role: 'HR & People Partner', slug: '/tali-ai-hr-people-partner', image: '/images/agent-thumbs/tali.webp' },
    { id: 'wright', name: 'Wright', role: 'Copywriting Expert', slug: '/wright-ai-copywriting-expert', image: '/images/agent-thumbs/wright.webp' },
    { id: 'zira', name: 'Zira', role: 'Support Expert', slug: '/zira-ai-support-expert', image: '/images/agent-thumbs/zira.webp' },
  ];

  // How It Works dropdown — the four capability pillars + the multi-model section double
  // as jump-links into the /how-it-works page sections (CP3, D6). Cross-page hash nav is
  // honored by FeaturesPage.
  const featurePillars = [
    { id: 'orchestration', name: 'They work like a team', sub: 'Multi-agent orchestration', href: '/how-it-works#orchestration' },
    { id: 'integrations', name: 'Runs on the tools you already use', sub: 'Workflows & integrations', href: '/how-it-works#integrations' },
    { id: 'control', name: 'You stay in control', sub: 'Human-in-the-loop', href: '/how-it-works#control' },
    { id: 'memory', name: 'It remembers your business', sub: 'Persistent memory', href: '/how-it-works#memory' },
    { id: 'models', name: 'The right model for every job', sub: 'Multi-model routing', href: '/how-it-works#models' },
  ];

  // Resources links (removed About Implify)
  const resources = [
    { id: 'blog', name: 'Blog', href: '/blog', isExternal: false },
    { id: 'faq', name: 'FAQ', href: '/faq', isExternal: false },
    { id: 'contact', name: 'Contact Us', href: 'mailto:support@implifyai.com', isExternal: true },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  // Logo URLs
  const darkLogo = "/images/logo-dark.webp";
  const whiteLogo = "/images/logo-white-header.webp";
  
  // At the top the header is transparent; white text/logo only over a dark hero.
  // Once scrolled the bar is solid white, so text/logo are always dark.
  const useWhiteStyle = isDarkBg && !isScrolled;
  const textColor = useWhiteStyle ? 'white' : '#0A0A23';
  const currentLogo = useWhiteStyle ? whiteLogo : darkLogo;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={currentLogo}
              alt="Implify" 
              className="h-7 md:h-8 w-auto transition-opacity duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6" ref={dropdownRef}>
            {/* Agents Dropdown (hover-open) */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('agents')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className="flex items-center gap-1 text-sm font-normal transition-all duration-200 hover:-translate-y-0.5"
                style={{ color: textColor }}
              >
                AI Teammates
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'agents' ? 'rotate-180' : ''}`} />
              </button>

              {/* Agents Dropdown Menu - Glassmorphism with better readability.
                  pt-4 is a transparent hover bridge so moving cursor into the menu
                  doesn't cross an empty gap and trigger mouseleave. */}
              {activeDropdown === 'agents' && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                  <div
                    className="w-[580px] p-5 rounded-2xl shadow-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      {agents.map((agent) => (
                        <Link
                          key={agent.id}
                          to={agent.slug}
                          onClick={() => setActiveDropdown(null)}
                          className="flex items-center gap-3 py-2.5 px-2 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                        >
                          <img
                            src={agent.image}
                            alt={agent.name}
                            className="w-10 h-10 rounded-lg object-cover transition-transform duration-200"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {agent.role}
                            </p>
                            <p className="text-xs text-gray-500">
                              {agent.name}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* How It Works Dropdown (hover-open; label links to /how-it-works hero) */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('features')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link
                to="/how-it-works"
                onClick={() => setActiveDropdown(null)}
                className="flex items-center gap-1 text-sm font-normal transition-all duration-200 hover:-translate-y-0.5"
                style={{ color: textColor }}
              >
                How It Works
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'features' ? 'rotate-180' : ''}`} />
              </Link>

              {activeDropdown === 'features' && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                  <div
                    className="w-80 p-3 rounded-2xl shadow-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    {/* Overview link to the Features hero, above the pillar jump-links */}
                    <Link
                      to="/how-it-works"
                      onClick={() => setActiveDropdown(null)}
                      className="block px-4 py-2.5 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                    >
                      <p className="text-sm font-medium text-gray-900">Overview</p>
                      <p className="text-xs text-gray-500">The system behind your AI team</p>
                    </Link>
                    <div className="my-1.5 border-t border-gray-200/70" />
                    {featurePillars.map((pillar) => (
                      <Link
                        key={pillar.id}
                        to={pillar.href}
                        onClick={() => setActiveDropdown(null)}
                        className="block px-4 py-2.5 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                      >
                        <p className="text-sm font-medium text-gray-900">{pillar.name}</p>
                        <p className="text-xs text-gray-500">{pillar.sub}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Use Cases Dropdown — two-column mega-menu (hover-open). No hub page, so the
                parent just opens the menu. Two co-equal columns: by department / by workflow. */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('useCases')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className="flex items-center gap-1 text-sm font-normal transition-all duration-200 hover:-translate-y-0.5"
                style={{ color: textColor }}
              >
                Use Cases
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'useCases' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'useCases' && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                  <div
                    className="w-[600px] p-5 rounded-2xl shadow-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    <div className="grid grid-cols-2 gap-x-6">
                      {/* By department */}
                      <div>
                        <p className="px-2 mb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-500">By department</p>
                        {ucDepartments.map((d) => (
                          <Link
                            key={d.slug}
                            to={`/use-cases/${d.slug}`}
                            onClick={() => setActiveDropdown(null)}
                            className="block px-2 py-2 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                          >
                            <p className="text-sm font-medium text-gray-900">{d.navLabel}</p>
                            <p className="text-xs text-gray-500">{d.menuDesc}</p>
                          </Link>
                        ))}
                      </div>
                      {/* By workflow */}
                      <div className="border-l border-gray-200/70 pl-6 -ml-px">
                        <p className="px-2 mb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-500">By workflow</p>
                        {ucWorkflows.map((w) => (
                          <Link
                            key={w.slug}
                            to={`/use-cases/${w.slug}`}
                            onClick={() => setActiveDropdown(null)}
                            className="block px-2 py-2 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                          >
                            <p className="text-sm font-medium text-gray-900">{w.navLabel}</p>
                            <p className="text-xs text-gray-500">{w.menuDesc}</p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Services — flat link, no dropdown (D4/D8). "Done for you" is the in-page
                positioning; nav label + slug = "Services" (user 2026-08-25, supersedes O4). */}
            <Link
              to="/services"
              className="text-sm font-normal transition-all duration-200 hover:-translate-y-0.5"
              style={{ color: textColor }}
            >
              Services
            </Link>

            {/* Pricing */}
            <Link
              to="/pricing"
              className="text-sm font-normal transition-all duration-200 hover:-translate-y-0.5"
              style={{ color: textColor }}
            >
              Pricing
            </Link>

            {/* Resources Dropdown (hover-open) */}
            <div
              className="relative"
              onMouseEnter={() => setActiveDropdown('resources')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className="flex items-center gap-1 text-sm font-normal transition-all duration-200 hover:-translate-y-0.5"
                style={{ color: textColor }}
              >
                Resources
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} />
              </button>

              {/* Resources Dropdown Menu - Glassmorphism with better readability */}
              {activeDropdown === 'resources' && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                  <div
                    className="w-48 p-3 rounded-xl shadow-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.92)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    }}
                  >
                    {resources.map((item) => (
                      item.isExternal ? (
                        <a
                          key={item.id}
                          href={item.href}
                          onClick={() => setActiveDropdown(null)}
                          className="block px-4 py-2.5 rounded-lg text-sm text-gray-700 transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                        >
                          {item.name}
                        </a>
                      ) : (
                        <Link
                          key={item.id}
                          to={item.href}
                          onClick={() => setActiveDropdown(null)}
                          className="block px-4 py-2.5 rounded-lg text-sm text-gray-700 transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                        >
                          {item.name}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* About Implify - standalone nav item */}
            <Link
              to="/about"
              className="text-sm font-normal transition-all duration-200 hover:-translate-y-0.5"
              style={{ color: textColor }}
            >
              About
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Mobile Hamburger Menu Button */}
            <button
              onClick={() => (isMobileMenuOpen ? closeMobileMenu() : setIsMobileMenuOpen(true))}
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{ color: useWhiteStyle ? 'white' : '#0A0A23' }}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            <Button
              onClick={() => window.location.href = 'https://app.implifyai.com'}
              className="text-xs md:text-sm font-normal px-3 md:px-6 py-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
              style={{ backgroundColor: '#FF5C5C', color: 'white' }}
              data-testid="get-started-btn"
            >
              <span className="hidden sm:inline">Get Started</span>
              <span className="sm:hidden">Start</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown - Glassmorphism effect */}
      <div 
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{
          background: 'rgba(255, 255, 255, 0.55)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }}
      >
        <nav className="flex flex-col px-4 py-4 space-y-0.5 overflow-y-auto max-h-[70vh]">
          {/* AI Teammates — accordion section */}
          <button
            onClick={() => toggleMobileSection('agents')}
            className="w-full flex items-center justify-between py-3 px-4 rounded-lg transition-colors hover:bg-gray-100"
            aria-expanded={mobileSection === 'agents'}
          >
            <span className="text-base font-medium text-gray-900">AI Teammates</span>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${mobileSection === 'agents' ? 'rotate-180' : ''}`} />
          </button>
          {mobileSection === 'agents' && (
            <div className="grid grid-cols-2 gap-2 px-2 pb-2">
              {agents.map((agent) => (
                <Link
                  key={agent.id}
                  to={agent.slug}
                  onClick={closeMobileMenu}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.02] hover:shadow-sm"
                >
                  <img
                    src={agent.image}
                    alt={agent.name}
                    className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 leading-tight">
                      {agent.role}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {agent.name}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="border-t border-gray-200/70" />

          {/* How It Works — accordion section */}
          <button
            onClick={() => toggleMobileSection('features')}
            className="w-full flex items-center justify-between py-3 px-4 rounded-lg transition-colors hover:bg-gray-100"
            aria-expanded={mobileSection === 'features'}
          >
            <span className="text-base font-medium text-gray-900">How It Works</span>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${mobileSection === 'features' ? 'rotate-180' : ''}`} />
          </button>
          {mobileSection === 'features' && (
            <div className="pb-2">
              <Link
                to="/how-it-works"
                onClick={closeMobileMenu}
                className="block py-2.5 px-4 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
              >
                <p className="text-sm font-medium text-gray-900 leading-tight">Overview</p>
                <p className="text-[11px] text-gray-500">The system behind your AI team</p>
              </Link>
              {featurePillars.map((pillar) => (
                <Link
                  key={pillar.id}
                  to={pillar.href}
                  onClick={closeMobileMenu}
                  className="block py-2.5 px-4 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
                >
                  <p className="text-sm font-medium text-gray-900 leading-tight">{pillar.name}</p>
                  <p className="text-[11px] text-gray-500">{pillar.sub}</p>
                </Link>
              ))}
            </div>
          )}

          <div className="border-t border-gray-200/70" />

          {/* Use Cases — accordion section (by department + by workflow, no hub link) */}
          <button
            onClick={() => toggleMobileSection('useCases')}
            className="w-full flex items-center justify-between py-3 px-4 rounded-lg transition-colors hover:bg-gray-100"
            aria-expanded={mobileSection === 'useCases'}
          >
            <span className="text-base font-medium text-gray-900">Use Cases</span>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${mobileSection === 'useCases' ? 'rotate-180' : ''}`} />
          </button>
          {mobileSection === 'useCases' && (
            <div className="pb-2">
              <p className="px-4 pt-1 pb-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">By department</p>
              {ucDepartments.map((d) => (
                <Link
                  key={d.slug}
                  to={`/use-cases/${d.slug}`}
                  onClick={closeMobileMenu}
                  className="block py-2 px-4 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
                >
                  <p className="text-sm font-medium text-gray-900 leading-tight">{d.navLabel}</p>
                  <p className="text-[11px] text-gray-500">{d.menuDesc}</p>
                </Link>
              ))}
              <p className="px-4 pt-2 pb-0.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">By workflow</p>
              {ucWorkflows.map((w) => (
                <Link
                  key={w.slug}
                  to={`/use-cases/${w.slug}`}
                  onClick={closeMobileMenu}
                  className="block py-2 px-4 rounded-lg transition-all duration-200 ease-out hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
                >
                  <p className="text-sm font-medium text-gray-900 leading-tight">{w.navLabel}</p>
                  <p className="text-[11px] text-gray-500">{w.menuDesc}</p>
                </Link>
              ))}
            </div>
          )}

          <div className="border-t border-gray-200/70" />

          {/* Services — flat link (D4/D8) */}
          <Link
            to="/services"
            onClick={closeMobileMenu}
            className="text-left text-base font-medium py-3 px-4 rounded-lg transition-all duration-200 ease-out text-gray-900 hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
          >
            Services
          </Link>

          <div className="border-t border-gray-200/70" />

          {/* Pricing — flat link */}
          <Link
            to="/pricing"
            onClick={closeMobileMenu}
            className="text-left text-base font-medium py-3 px-4 rounded-lg transition-all duration-200 ease-out text-gray-900 hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
            data-testid="mobile-nav-pricing"
          >
            Pricing
          </Link>

          <div className="border-t border-gray-200/70" />

          {/* Resources — accordion section */}
          <button
            onClick={() => toggleMobileSection('resources')}
            className="w-full flex items-center justify-between py-3 px-4 rounded-lg transition-colors hover:bg-gray-100"
            aria-expanded={mobileSection === 'resources'}
          >
            <span className="text-base font-medium text-gray-900">Resources</span>
            <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${mobileSection === 'resources' ? 'rotate-180' : ''}`} />
          </button>
          {mobileSection === 'resources' && (
            <div className="pb-2">
              {resources.map((item) => (
                item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.href}
                    onClick={closeMobileMenu}
                    className="block text-sm font-normal py-2.5 px-4 rounded-lg transition-all duration-200 ease-out text-gray-900 hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={closeMobileMenu}
                    className="block text-sm font-normal py-2.5 px-4 rounded-lg transition-all duration-200 ease-out text-gray-900 hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          )}

          <div className="border-t border-gray-200/70" />

          {/* About — flat link */}
          <Link
            to="/about"
            onClick={closeMobileMenu}
            className="text-left text-base font-medium py-3 px-4 rounded-lg transition-all duration-200 ease-out text-gray-900 hover:bg-gray-100 hover:scale-[1.01] hover:shadow-sm"
          >
            About Implify
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
